import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getCustomer, getPointHistory } from '../../../api/customer.api';
import { formatPoints } from '../../../utils/formatters';
import styles from '../../../assets/scss/pages/CustomerHistory.module.scss';

function formatDateTime(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function CustomerPointHistory() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [custRes, pointsRes] = await Promise.all([
                getCustomer(id),
                getPointHistory(id, { page: 1, limit: 1000 }),
            ]);
            setCustomer(custRes.data.customer || custRes.data);
            const list = pointsRes.data?.data || pointsRes.data || [];
            setTransactions(Array.isArray(list) ? list.filter((t) => t.billId) : []);
        } catch (err) {
            console.error('Error fetching point history:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filtered = transactions.filter((t) => {
        if (filter === 'earn') return t.type === 'earn';
        if (filter === 'redeem') return t.type === 'redeem';
        return true;
    });

    const earnCount = transactions.filter((t) => t.type === 'earn').length;
    const redeemCount = transactions.filter((t) => t.type === 'redeem').length;

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <div>
                        <h1 className={styles.headerTitle}>Lịch sử điểm</h1>
                        <span className={styles.headerSub}>{customer?.name || 'Khách hàng'}</span>
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className={styles.summary}>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryEarn}>+{earnCount} nhận điểm</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryRedeem}>-{redeemCount} dùng điểm</span>
                </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                {['all', 'earn', 'redeem'].map((f) => (
                    <button
                        key={f}
                        className={`${styles.filterBtn}${filter === f ? ` ${styles.filterBtnActive}` : ''}`}
                        onClick={() => setFilter(f)}
                    >
                        {f === 'all' ? `Tất cả (${transactions.length})` : f === 'earn' ? `Nhận điểm (${earnCount})` : `Dùng điểm (${redeemCount})`}
                    </button>
                ))}
            </div>

            {/* List */}
            <div className={styles.card}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className={styles.empty}>Chưa có giao dịch điểm</div>
                ) : (
                    <div className={styles.list}>
                        {filtered.map((t) => (
                            <div
                                key={t.id}
                                className={styles.item}
                                onClick={() => t.billId && navigate(`/admin/bills/${t.billId}`)}
                            >
                                <div className={styles.itemTop}>
                                    <div>
                                        <span className={styles.billCode}>
                                            {t.billId ? `HD${String(t.billId).padStart(6, '0')}` : '-'}
                                        </span>
                                        <span className={styles.meta}>{formatDateTime(t.createdAt)}</span>
                                    </div>
                                    <span className={t.type === 'earn' ? styles.pointEarn : styles.pointRedeem}>
                                        {t.type === 'earn' ? '+' : '-'}{formatPoints(Math.abs(t.points))}
                                    </span>
                                </div>
                                <div className={styles.itemBottom}>
                                    <span className={styles.services}>
                                        {t.description || (t.type === 'earn' ? 'Tích điểm từ hoá đơn' : 'Sử dụng điểm cho hoá đơn')}
                                    </span>
                                    <span className={styles.balance}>
                                        Số dư: {formatPoints(t.balanceAfter)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
